import { suggestQty, tinDays, DOSING } from '@theme/vw-dosing';

/**
 * VitaWoof PDP buy box (Seal-style subscribe / one-time selector).
 *
 * GOAL 1: Variant price updates via Horizon's correct 'variant:update' event.
 * Mode (abo/once) is persisted in sessionStorage so it survives any re-render.
 * Per-variant plan prices are stored in data-variants JSON (planPrice field).
 * FIX 1: #commit() now dispatches { label, variantId, planId } so the sticky
 * bar can sync its own form's [name="id"] and [name="selling_plan"].
 * FIX 3: Per-day line is updated on every commit using planPriceCents / days / 100.
 */

const SIZE_KEY = 'vwDogSize';
const MODE_KEY = 'vwBuyMode';
const SIZE_LABEL = { small: 'Klein', medium: 'Mittel', large: 'Gross' };

class VwPdpAbo extends HTMLElement {
  connectedCallback() {
    // Guard against double-init if somehow called twice.
    if (this._initialized) {
      this.#reinit();
      return;
    }
    this._initialized = true;

    this.cfg = {
      snacks: parseFloat(this.dataset.snacks) || DOSING.snacks,
      dose: {
        small: parseFloat(this.dataset.doseSmall) || DOSING.dose.small,
        medium: parseFloat(this.dataset.doseMedium) || DOSING.dose.medium,
        large: parseFloat(this.dataset.doseLarge) || DOSING.dose.large,
      },
    };
    this.price = parseInt(this.dataset.price, 10) || 0;
    try {
      this.plans = JSON.parse(this.dataset.plans || '[]');
    } catch (_) {
      this.plans = [];
    }

    // Variant price map: each entry has { id, price, compare_at_price, planPrice, tins, days }.
    // planPrice is the Seal allocation price for this variant (added in liquid).
    // tins and days are derived from the variant title in liquid.
    try {
      this.variantMap = JSON.parse(this.dataset.variants || '[]').reduce((m, v) => {
        m[v.id] = v;
        return m;
      }, {});
    } catch (_) {
      this.variantMap = {};
    }

    // Determine the current variant id from the product form's [name="id"] input.
    this._currentVariantId = this.#readCurrentVariantId();

    this.planInput = this.querySelector('[data-vw-plan-input]');
    this.sizeInput = this.querySelector('[data-vw-size-input]');
    this.priceNowEl = this.querySelector('[data-vw-price-now]');
    this.priceWasEl = this.querySelector('[data-vw-price-was]');
    this.oncePriceEl = this.querySelector('[data-vw-once-price]');
    this.aboPriceEl = this.querySelector('[data-vw-abo-price]');
    this.perdayEl = this.querySelector('[data-vw-perday]');
    this.cards = Array.from(this.querySelectorAll('[data-vw-card]'));

    // Restore previously chosen dog size.
    let size = 'medium';
    try {
      const s = localStorage.getItem(SIZE_KEY);
      if (s && SIZE_LABEL[s]) size = s;
    } catch (_) { /* ignore */ }

    // Restore previously chosen mode (abo/once) — survives morphs / re-renders.
    let savedMode = 'abo';
    try {
      const m = sessionStorage.getItem(MODE_KEY);
      if (m === 'once' || m === 'abo') savedMode = m;
    } catch (_) { /* ignore */ }

    const def = this.#defaultPlan();
    this.state = {
      size,
      mode: savedMode,
      planId: def ? def.id : null,
      qty: 1,
      doseOpened: false,
    };

    this.addEventListener('click', this.#onClick.bind(this));
    this.addEventListener('keydown', this.#onKeydown.bind(this));

    // Re-assert quantity at submit time.
    const form = this.closest('form');
    if (form) form.addEventListener('submit', () => this.#setNativeQty(this.state.qty), true);

    // GOAL 1: Listen for Horizon's correct variant:update event.
    this.#listenForVariantUpdate();

    this.#initDosePopup();
    this.#renderPrices();
    this.#commit();
  }

  // Called by Horizon's morph onAfterUpdate when the element is patched in-place.
  updatedCallback() {
    this.price = parseInt(this.dataset.price, 10) || 0;
    try {
      this.plans = JSON.parse(this.dataset.plans || '[]');
    } catch (_) { this.plans = []; }
    try {
      this.variantMap = JSON.parse(this.dataset.variants || '[]').reduce((m, v) => {
        m[v.id] = v;
        return m;
      }, {});
    } catch (_) { this.variantMap = {}; }
    const def = this.#defaultPlan();
    if (!this.state.planId) this.state.planId = def ? def.id : null;
    this.#renderPrices();
    this.#commit();
  }

  // Idempotent re-init for cases where connectedCallback fires again.
  #reinit() {
    this.price = parseInt(this.dataset.price, 10) || 0;
    try { this.plans = JSON.parse(this.dataset.plans || '[]'); } catch (_) { this.plans = []; }
    try {
      this.variantMap = JSON.parse(this.dataset.variants || '[]').reduce((m, v) => {
        m[v.id] = v; return m;
      }, {});
    } catch (_) { this.variantMap = {}; }
    this.planInput = this.querySelector('[data-vw-plan-input]');
    this.sizeInput = this.querySelector('[data-vw-size-input]');
    this.priceNowEl = this.querySelector('[data-vw-price-now]');
    this.priceWasEl = this.querySelector('[data-vw-price-was]');
    this.oncePriceEl = this.querySelector('[data-vw-once-price]');
    this.aboPriceEl = this.querySelector('[data-vw-abo-price]');
    this.perdayEl = this.querySelector('[data-vw-perday]');
    this.cards = Array.from(this.querySelectorAll('[data-vw-card]'));
    if (!this.state) {
      let size = 'medium';
      try { const s = localStorage.getItem(SIZE_KEY); if (s && SIZE_LABEL[s]) size = s; } catch (_) {}
      let savedMode = 'abo';
      try { const m = sessionStorage.getItem(MODE_KEY); if (m === 'once' || m === 'abo') savedMode = m; } catch (_) {}
      const def = this.#defaultPlan();
      this.state = { size, mode: savedMode, planId: def ? def.id : null, qty: 1, doseOpened: false };
    }
    this.#renderPrices();
    this.#commit();
  }

  // Read the current variant id from the product form.
  #readCurrentVariantId() {
    const form = this.closest('form');
    if (!form) return null;
    const input = form.querySelector('input[name="id"]');
    return input ? parseInt(input.value, 10) || null : null;
  }

  // GOAL 1 — correct Horizon integration.
  #listenForVariantUpdate() {
    const section = this.closest('.shopify-section');
    const target = section || document;

    target.addEventListener('variant:update', (e) => {
      const variant = e.detail && e.detail.resource;
      if (variant && variant.id) this.#onVariantChange(variant);
    });

    target.addEventListener('variant-change', (e) => {
      const v = e.detail && (e.detail.variant || e.detail);
      if (v && v.id && v.price != null) this.#onVariantChange(v);
    });

    // MutationObserver fallback on the form's [name="id"] input.
    const form = this.closest('form');
    if (form) {
      const idInput = form.querySelector('input[name="id"]');
      if (idInput) {
        const obs = new MutationObserver(() => {
          const vid = parseInt(idInput.value, 10);
          if (vid && this.variantMap && this.variantMap[vid]) {
            this._currentVariantId = vid;
            this.#onVariantChangeById(vid);
          }
        });
        obs.observe(idInput, { attributes: true, attributeFilter: ['value'] });
        this._variantObs = obs;
      }
    }
  }

  // Called when we have a full variant object (from variant:update).
  #onVariantChange(variant) {
    const vid = variant.id;
    this._currentVariantId = vid;
    const vData = (this.variantMap && this.variantMap[vid]) || null;

    this.price = (vData && vData.price) || variant.price || this.price;

    const planPrice = (vData && vData.planPrice) ? vData.planPrice : Math.round(this.price * 0.8);
    if (this.plans.length > 0) {
      this.plans[0].price = planPrice;
    }

    this.#renderPrices();
    this.#commit();
  }

  // Called when we only have a variant ID (MutationObserver fallback).
  #onVariantChangeById(variantId) {
    const v = this.variantMap && this.variantMap[variantId];
    if (!v) return;
    this._currentVariantId = variantId;
    this.price = v.price;
    const planPrice = v.planPrice ? v.planPrice : Math.round(v.price * 0.8);
    if (this.plans.length > 0) this.plans[0].price = planPrice;
    this.#renderPrices();
    this.#commit();
  }

  #money(cents) {
    return 'CHF ' + (cents / 100).toFixed(2);
  }

  #defaultPlan() {
    return this.plans.reduce((best, p) => (best && best.price <= p.price ? best : p), null);
  }

  #activePlan() {
    if (this.state.mode !== 'abo') return null;
    return this.plans.find((p) => p.id === this.state.planId) || this.#defaultPlan();
  }

  // FIX 3: compute per-day price from current variant's planPrice and days.
  // planPrice is in CENTS — divide by days AND by 100 to get CHF per day.
  // 1 Dose: 3192 / 60 / 100 = 0.53 | 2 Dosen: 5432 / 120 / 100 = 0.45 | 3 Dosen: 7192 / 180 / 100 = 0.40
  #updatePerDay() {
    if (!this.perdayEl) return;
    const vid = this._currentVariantId;
    const vData = vid && this.variantMap ? this.variantMap[vid] : null;
    // days: from liquid-built 'days' field, fallback 60
    const days = (vData && vData.days) ? vData.days : 60;
    // planPrice: current first plan's price in cents (already updated on variant change)
    const plan = this.#defaultPlan();
    const planPrice = plan ? plan.price : Math.round(this.price * 0.8);
    const perDay = planPrice / days / 100;
    this.perdayEl.textContent = 'ab CHF ' + perDay.toFixed(2) + ' pro Tag im Abo';
  }

  #onClick(event) {
    const card = event.target.closest('[data-vw-card]');
    if (card) this.#setMode(card.dataset.mode);
  }

  #onKeydown(event) {
    const card = event.target.closest('[data-vw-card]');
    if (!card) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.#setMode(card.dataset.mode);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.#focusCard(1);
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#focusCard(-1);
    }
  }

  #focusCard(dir) {
    const idx = this.cards.findIndex((c) => c.classList.contains('is-on'));
    const next = this.cards[(idx + dir + this.cards.length) % this.cards.length];
    if (next) {
      next.focus();
      this.#setMode(next.dataset.mode);
    }
  }

  #setMode(mode) {
    if (mode !== 'abo' && mode !== 'once') return;
    this.state.mode = mode;
    try { sessionStorage.setItem(MODE_KEY, mode); } catch (_) { /* ignore */ }
    if (mode === 'abo' && !this.state.planId) {
      const def = this.#defaultPlan();
      this.state.planId = def ? def.id : null;
    }
    this.#renderPrices();
    this.#commit();
  }

  #renderPrices() {
    const plan = this.#defaultPlan();
    const aboPrice = plan ? plan.price : this.price;

    if (this.state.mode === 'abo') {
      if (this.priceNowEl) this.priceNowEl.textContent = this.#money(aboPrice);
      if (this.priceWasEl) {
        this.priceWasEl.textContent = this.#money(this.price);
        this.priceWasEl.hidden = aboPrice >= this.price;
      }
    } else {
      if (this.priceNowEl) this.priceNowEl.textContent = this.#money(this.price);
      if (this.priceWasEl) this.priceWasEl.hidden = true;
    }

    if (this.oncePriceEl) {
      this.oncePriceEl.innerHTML = `<strong>${this.#money(this.price)}</strong>`;
    }
    if (this.aboPriceEl) {
      this.aboPriceEl.innerHTML = `<s>${this.#money(this.price)}</s><strong>${this.#money(aboPrice)}</strong>`;
    }

    this.cards.forEach((c) => {
      const on = c.dataset.mode === this.state.mode;
      c.classList.toggle('is-on', on);
      c.setAttribute('aria-checked', on ? 'true' : 'false');
      c.tabIndex = on ? 0 : -1;
    });
  }

  #setNativeQty(qty) {
    const form = this.closest('form');
    if (!form) return;
    const input = form.querySelector('input[name="quantity"]');
    if (input) {
      input.value = String(qty);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  // FIX 1: #commit dispatches enriched detail: { label, variantId, planId }
  // variantId = current variant; planId = selling plan id (null in once mode).
  #commit() {
    if (this.state.mode === 'abo' && this.state.planId) {
      this.planInput.value = String(this.state.planId);
      this.planInput.disabled = false;
    } else {
      this.planInput.value = '';
      this.planInput.disabled = true;
    }
    if (this.sizeInput) this.sizeInput.value = SIZE_LABEL[this.state.size] || 'Mittel';
    this.#setNativeQty(this.state.qty);

    // FIX 3: update per-day line
    this.#updatePerDay();

    let label;
    if (this.state.mode === 'abo') {
      const plan = this.#activePlan();
      const unit = plan ? plan.price : this.price;
      label = `${this.#money(unit)} \u00b7 monatlich`;
    } else {
      label = `${this.#money(this.price)} \u00b7 einmalig`;
    }
    this.dataset.label = label;

    // FIX 1: include variantId and planId so sticky bar can sync its own form.
    const variantId = this._currentVariantId || this.#readCurrentVariantId();
    const planId = (this.state.mode === 'abo' && this.state.planId) ? this.state.planId : null;
    this.dispatchEvent(new CustomEvent('vw:plan-change', {
      bubbles: true,
      detail: { text: label, variantId, planId },
    }));
  }

  /* ---------------- Dose popup ---------------- */

  #initDosePopup() {
    this.doseModal = this.querySelector('[data-vw-dose-modal]');
    this.doseResult = this.querySelector('[data-vw-dose-result]');
    this.doseOpenBtn = this.querySelector('[data-vw-dose-open]');
    if (!this.doseModal) return;

    this.doseSize = this.state.size;
    this.#syncDoseSizes();
    this.#renderDoseResult();

    this.doseModal.addEventListener('click', (e) => {
      const sizeBtn = e.target.closest('[data-vw-dose-size]');
      if (sizeBtn) {
        this.doseSize = sizeBtn.dataset.vwDoseSize;
        this.#syncDoseSizes();
        this.#renderDoseResult();
        return;
      }
      if (e.target.closest('[data-vw-dose-apply]')) { this.#applyDose(); return; }
      if (e.target.closest('[data-vw-dose-close]') || e.target === this.doseModal) { this.#closeDose(); }
    });

    if (this.doseOpenBtn) this.doseOpenBtn.addEventListener('click', () => this.#openDose());
    this._doseKeydown = (e) => this.#onDoseKeydown(e);
  }

  #syncDoseSizes() {
    this.querySelectorAll('[data-vw-dose-size]').forEach((b) => {
      b.setAttribute('aria-checked', b.dataset.vwDoseSize === this.doseSize ? 'true' : 'false');
    });
  }

  #renderDoseResult() {
    if (!this.doseResult) return;
    const qty = suggestQty(this.doseSize, 1, this.cfg);
    const days = Math.round(tinDays(this.doseSize, this.cfg) * qty);
    const dose = qty === 1 ? 'Dose' : 'Dosen';
    this.doseResult.textContent = `Empfehlung: ${qty} ${dose} pro Lieferung, reicht ca. ${days} Tage.`;
  }

  #applyDose() {
    this.state.size = this.doseSize;
    this.state.doseOpened = true;
    this.state.qty = suggestQty(this.doseSize, 1, this.cfg);
    try { localStorage.setItem(SIZE_KEY, this.doseSize); } catch (_) { /* ignore */ }
    this.#commit();
    this.#closeDose();
  }

  #openDose() {
    if (!this.doseModal) return;
    this.doseSize = this.state.size;
    this.#syncDoseSizes();
    this.#renderDoseResult();
    this._doseTrigger = document.activeElement;
    this.doseModal.hidden = false;
    this._dosePrevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    this.#setBackgroundInert(true);
    const first = this.doseModal.querySelector('[data-vw-dose-close-btn]');
    this.#focus(first || this.doseModal);
    document.addEventListener('keydown', this._doseKeydown, true);
  }

  #closeDose() {
    if (!this.doseModal || this.doseModal.hidden) return;
    this.#setBackgroundInert(false);
    this.doseModal.hidden = true;
    document.documentElement.style.overflow = this._dosePrevOverflow || '';
    document.removeEventListener('keydown', this._doseKeydown, true);
    this.#focus(this._doseTrigger);
    this._doseTrigger = null;
  }

  #focus(el) {
    if (!el || typeof el.focus !== 'function') return;
    try { el.focus({ preventScroll: true }); } catch (_) { el.focus(); }
  }

  #doseFocusable() {
    const sel = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';
    return Array.from(this.doseModal.querySelectorAll(sel)).filter(
      (n) => n.offsetWidth || n.offsetHeight || n.getClientRects().length
    );
  }

  #onDoseKeydown(e) {
    if (!this.doseModal || this.doseModal.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); this.#closeDose(); return; }
    if (e.key === 'Tab') {
      const f = this.#doseFocusable();
      if (!f.length) { e.preventDefault(); return; }
      const first = f[0], last = f[f.length - 1], active = document.activeElement;
      if (e.shiftKey && (active === first || !this.doseModal.contains(active))) {
        e.preventDefault(); this.#focus(last);
      } else if (!e.shiftKey && (active === last || !this.doseModal.contains(active))) {
        e.preventDefault(); this.#focus(first);
      }
    }
  }

  #setBackgroundInert(on) {
    if (on) {
      this._inerted = [];
      document.querySelectorAll('.shopify-section').forEach((el) => {
        if (el === this.doseModal || el.contains(this.doseModal)) return;
        this._inerted.push({ el, hadInert: el.hasAttribute('inert'), hadAria: el.getAttribute('aria-hidden') });
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
      });
    } else if (this._inerted) {
      this._inerted.forEach((rec) => {
        if (!rec.hadInert) rec.el.removeAttribute('inert');
        if (rec.hadAria === null) rec.el.removeAttribute('aria-hidden');
        else rec.el.setAttribute('aria-hidden', rec.hadAria);
      });
      this._inerted = [];
    }
  }
}

if (!customElements.get('vw-pdp-abo')) {
  customElements.define('vw-pdp-abo', VwPdpAbo);
}
